/**
 * Breadcrumb — navigation trail for wayfinding.
 *
 * Shows the current page's location in the app hierarchy.
 * Last item is not a link (current page).
 */

import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import { ChevronRight } from 'lucide-react'

export interface BreadcrumbItem {
  /** Display label. */
  label: string
  /** Navigation path. Omit for the current (last) item. */
  href?: string
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[]
  className?: string
}

/** Breadcrumb navigation trail. */
export function Breadcrumb({ items, className }: BreadcrumbProps) {
  if (items.length === 0) return null

  return (
    <nav
      aria-label="Breadcrumb"
      className={clsx('flex items-center gap-1.5 text-xs text-content-tertiary', className)}
    >
      {items.map((item, i) => {
        const isLast = i === items.length - 1
        return (
          <span key={item.label} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="w-3 h-3" />}
            {item.href && !isLast ? (
              <Link to={item.href} className="hover:text-content transition-colors">
                {item.label}
              </Link>
            ) : (
              <span className={clsx(isLast && 'text-content-secondary font-medium')}>
                {item.label}
              </span>
            )}
          </span>
        )
      })}
    </nav>
  )
}
